// @ts-nocheck
import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import { ANNOTATION_TAGS } from './pdfUtils';
import { useTTS } from '../hooks/useTTS';
import { logger } from '../utils/logger';
import type { QASession } from '../types/pdf-viewer';

const PRESET_PROMPTS: string[] = [
  "帮忙翻译这段话，注意专业用词的准确性，然后分析这段话的内容，如果需要通过例子解释的话，请举例",
  "这段内容的核心观点是什么？请详细解释",
  "这段话有什么重要信息？请总结要点",
  "请解释这段内容中的专业术语和概念",
  "这段话的逻辑结构是怎样的？请分析论证过程",
  "请翻译并解释这段内容：\n1. 逐句翻译，一句【英文】，一句【中文】\n2. 这段在说什么核心观点？\n3. 有哪些关键信息？\n4. 与常见概念的关联"
];

interface QASessionCardProps {
  session: QASession;
  onUpdateSession: (id: string, updates: Partial<QASession>) => void;
  onRemoveSession: (id: string) => void;
  onAskQuestion: (id: string) => void;
  onSaveAsAnnotation: (id: string) => void;
  onStartQuickScan: (id: string) => void;
  onStartDeepDive: (id: string) => void;
  onConvertToDeepDive: (id: string) => void;
  onSendToCanvas: (id: string, mode: string) => void;
  index: number;
  [key: string]: any;
}

// 单个问答会话卡片组件
function QASessionCard({
  session,
  onUpdateSession,
  onRemoveSession,
  onAskQuestion,
  onSaveAsAnnotation,
  onStartQuickScan,
  onStartDeepDive,
  onConvertToDeepDive,
  onSendToCanvas,
  index
}: QASessionCardProps) {
  const { id, sessionName, editableText, position, question, answer, status, selectedTag, hasHighlight, mode, aiQuickAnalysis, aiDeepAnalysis, isAnalyzing, quickScanSaved, isMinimized } = session;

  // 每个卡片独立的 TTS 状态
  const { isGenerating: isGeneratingSpeech, currentAudio, generateSpeech, stopAudio } = useTTS();
  const [isDragging, setIsDragging] = React.useState<boolean>(false);
  const [dragOffset, setDragOffset] = React.useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [cardPosition, setCardPosition] = React.useState<{ x: number; y: number } | null>(null);
  const [isEditingName, setIsEditingName] = React.useState<boolean>(false);
  const [tempSessionName, setTempSessionName] = React.useState<string>(sessionName || '');

  // 计算卡片位置
  const getCardStyle = (): React.CSSProperties => {
    const baseStyle: React.CSSProperties = {
      position: 'fixed',
      width: isMinimized ? '350px' : '400px',
      maxHeight: isMinimized ? 'auto' : '600px',
      backgroundColor: 'white',
      border: '1px solid #ddd',
      borderRadius: '8px',
      boxShadow: isDragging ? '0 8px 32px rgba(0,0,0,0.3)' : '0 4px 20px rgba(0,0,0,0.15)',
      zIndex: isDragging ? 1100 : 1000,
      overflow: 'hidden',
      cursor: isDragging ? 'grabbing' : 'default',
      userSelect: isDragging ? 'none' : 'auto',
      transition: 'width 0.3s ease, max-height 0.3s ease'
    };

    if (cardPosition) {
      return { ...baseStyle, left: `${cardPosition.x}px`, top: `${cardPosition.y}px` };
    }

    if (position) {
      let left = position.x;
      let top = position.y;
      const cardWidth = 400;
      const cardHeight = Math.min(600, window.innerHeight * 0.8);
      if (left + cardWidth > window.innerWidth) left = Math.max(10, window.innerWidth - cardWidth - 10);
      if (top + cardHeight > window.innerHeight) top = Math.max(10, window.innerHeight - cardHeight - 10);
      const offset = index * 30;
      left = Math.min(left + offset, window.innerWidth - cardWidth - 10);
      top = Math.min(top + offset, window.innerHeight - cardHeight - 10);
      return { ...baseStyle, left: `${Math.max(10, left)}px`, top: `${Math.max(10, top)}px`, maxHeight: `${cardHeight}px` };
    }

    const offset = index * 40;
    return { ...baseStyle, left: `${Math.min(50 + offset, 70)}%`, top: `${Math.min(20 + offset / 2, 40)}%`, transform: 'translate(-50%, -50%)' };
  };

  // 拖拽处理
  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (
      (e.target as HTMLElement).closest('.session-name-edit') ||
      (e.target as HTMLElement).closest('.close-btn') ||
      (e.target as HTMLElement).tagName === 'INPUT' ||
      (e.target as HTMLElement).tagName === 'BUTTON' ||
      (e.target as HTMLElement).closest('.qa-session-content')
    ) {
      return;
    }
    if (!(e.target as HTMLElement).closest('.qa-session-header')) return;
    e.preventDefault();
    setIsDragging(true);
    const rect = e.currentTarget.getBoundingClientRect();
    setDragOffset({ x: e.clientX - rect.left, y: e.clientY - rect.top });
  };

  React.useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging) return;
      e.preventDefault();
      const newX = e.clientX - dragOffset.x;
      const newY = e.clientY - dragOffset.y;
      const maxX = window.innerWidth - 400;
      const maxY = window.innerHeight - 100;
      setCardPosition({ x: Math.max(0, Math.min(newX, maxX)), y: Math.max(0, Math.min(newY, maxY)) });
    };
    const handleMouseUp = () => { setIsDragging(false); };
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.userSelect = 'none';
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.body.style.userSelect = '';
      };
    }
  }, [isDragging, dragOffset]);

  const getStatusColor = (): string => {
    switch (status) {
      case 'questioning': return '#007bff';
      case 'answered': return '#28a745';
      case 'error': return '#dc3545';
      default: return '#6c757d';
    }
  };

  const handleNameEdit = (e: React.MouseEvent) => {
    e.preventDefault(); e.stopPropagation();
    setTempSessionName(sessionName || '');
    setIsEditingName(true);
  };

  const handleNameSave = () => {
    const newName = tempSessionName.trim();
    if (newName && newName !== sessionName) { onUpdateSession(id, { sessionName: newName }); }
    setIsEditingName(false);
  };

  const handleNameCancel = () => { setTempSessionName(sessionName || ''); setIsEditingName(false); };

  const handleNameKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') { e.preventDefault(); e.stopPropagation(); handleNameSave(); }
    else if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); handleNameCancel(); }
  };

  const handleNameInputChange = (e: React.ChangeEvent<HTMLInputElement>) => { setTempSessionName(e.target.value); };

  const handleSaveAnnotation = React.useCallback((e: React.MouseEvent) => {
    e.preventDefault(); e.stopPropagation();
    logger.log('保存标注被点击，会话ID:', id);
    if (typeof onSaveAsAnnotation !== 'function') {
      console.error('onSaveAsAnnotation 不是函数');
      alert('保存功能异常，请刷新页面重试');
      return;
    }
    const hasContent = aiQuickAnalysis || aiDeepAnalysis || (answer && answer.trim().length > 0);
    if (!hasContent) { logger.log('没有可保存的内容'); alert('请先获取AI分析或回答'); return; }
    try { logger.log('调用保存函数'); onSaveAsAnnotation(id); }
    catch (error: any) { console.error('保存过程中出错:', error); alert('保存失败: ' + error.message); }
  }, [id, answer, aiQuickAnalysis, aiDeepAnalysis, onSaveAsAnnotation]);

  return (
    <div className="qa-session-card" style={getCardStyle()} onMouseDown={handleMouseDown}>
      <div className="qa-session-header" style={{ borderLeftColor: getStatusColor(), cursor: isDragging ? 'grabbing' : 'grab' }}>
        <div className="session-header-content">
          <div className="session-title-section">
            {isEditingName ? (
              <div className="session-name-edit" onClick={(e: React.MouseEvent) => e.stopPropagation()}>
                <input type="text" value={tempSessionName} onChange={handleNameInputChange} onKeyDown={handleNameKeyDown} onBlur={handleNameSave} className="session-name-input" placeholder="输入会话名字..." autoFocus style={{ pointerEvents: 'auto' }} />
              </div>
            ) : (
              <div className="session-name-display" onClick={handleNameEdit}>
                <span className="session-name" title="点击编辑会话名字">{sessionName || '未命名会话'}</span>
                <button type="button" className="edit-name-btn" onClick={handleNameEdit} title="编辑会话名字" style={{ pointerEvents: 'auto' }}>&#9999;&#65039;</button>
              </div>
            )}
          </div>
          <div className="session-status-section">
            <span className="session-status" style={{ color: getStatusColor() }}>
              {status === 'questioning' && '思考中...'}
              {status === 'answered' && '已完成'}
              {status === 'error' && '出错了'}
              {status === 'ready' && '准备中'}
            </span>
            <span className="session-page">第{session.page}页</span>
            {hasHighlight && <span className="session-highlight-indicator">高亮</span>}
            {quickScanSaved && <span className="session-highlight-indicator" style={{ background: '#28a745' }}>已保存</span>}
          </div>
        </div>
        <div className="header-controls">
          <span className="drag-handle" title="拖拽移动">&#8942;&#8942;</span>
          <button onClick={(e: React.MouseEvent) => { e.preventDefault(); e.stopPropagation(); onUpdateSession(id, { isMinimized: !isMinimized }); }} className="minimize-btn" style={{ pointerEvents: 'auto' }} title={isMinimized ? '展开' : '最小化'}>{isMinimized ? '\u25A2' : '\u2014'}</button>
          <button onClick={(e: React.MouseEvent) => { e.preventDefault(); e.stopPropagation(); onRemoveSession(id); }} className="close-btn" style={{ pointerEvents: 'auto' }}>&times;</button>
        </div>
      </div>

      {!isMinimized && (
        <div className="qa-session-content" style={{ pointerEvents: 'auto' }}>
          {/* 快速浏览模式 */}
          {mode === 'quick_scan' && (
          <div className="quick-scan-mode">
            <div className="selected-content">
              <strong>选中内容：</strong>
              <textarea value={editableText} onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => onUpdateSession(id, { editableText: e.target.value })} placeholder="选中的文本内容..." rows={3} className="editable-text" disabled={isAnalyzing} style={{ pointerEvents: 'auto' }} />
            </div>
            {!aiQuickAnalysis && !isAnalyzing && (
              <div className="start-analysis-section">
                <button onClick={() => onStartQuickScan(id)} className="start-analysis-btn" disabled={!editableText.trim()} style={{ pointerEvents: 'auto' }}>开始快速浏览分析</button>
                <small style={{ display: 'block', marginTop: '0.5rem', color: '#666', fontSize: '12px' }}>提示：可以先修改上方文本内容，然后点击开始分析</small>
              </div>
            )}
            {isAnalyzing && (<div className="analyzing-status"><div className="mini-spinner"></div><span>正在生成快速浏览分析...</span></div>)}
            {aiQuickAnalysis && (
              <div className="quick-scan-result">
                <div className="quick-scan-header">
                  <h4>快速浏览分析</h4>
                  <div className="speech-controls">
                    <button onClick={(e: React.MouseEvent) => { e.preventDefault(); e.stopPropagation(); generateSpeech(aiQuickAnalysis); }} disabled={isGeneratingSpeech} className="speech-btn" title="转为语音" style={{ pointerEvents: 'auto' }}>{isGeneratingSpeech ? '生成中...' : '🔊'}</button>
                    {currentAudio && (<button onClick={(e: React.MouseEvent) => { e.preventDefault(); e.stopPropagation(); stopAudio(); }} className="stop-speech-btn" title="停止播放" style={{ pointerEvents: 'auto' }}>⏹</button>)}
                  </div>
                </div>
                <div className="analysis-content"><ReactMarkdown remarkPlugins={[remarkMath]} rehypePlugins={[rehypeKatex]}>{aiQuickAnalysis}</ReactMarkdown></div>
                <div className="quick-scan-actions">
                  <button onClick={() => onConvertToDeepDive(id)} className="deep-dive-btn" disabled={isAnalyzing} style={{ pointerEvents: 'auto' }}>转为深入理解</button>
                  <button onClick={(e: React.MouseEvent) => { e.preventDefault(); e.stopPropagation(); onSendToCanvas(id, 'quick_scan'); }} className="send-to-canvas-btn" disabled={isAnalyzing || session.sentQuickScanToCanvas} style={{ pointerEvents: 'auto' }}>{session.sentQuickScanToCanvas ? '\u2713 已发送' : '🎨 发送到画布'}</button>
                </div>
              </div>
            )}
            {aiQuickAnalysis && (
              <div className="follow-up-section">
                <label>继续追问：</label>
                <textarea value={question} onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => onUpdateSession(id, { question: e.target.value })} placeholder="有疑问？继续追问..." rows={2} disabled={status === 'questioning'} style={{ pointerEvents: 'auto' }} />
                <button onClick={(e: React.MouseEvent) => { e.preventDefault(); e.stopPropagation(); onAskQuestion(id); }} disabled={status === 'questioning' || !question.trim()} className="ask-btn" style={{ pointerEvents: 'auto' }}>{status === 'questioning' ? '思考中...' : '追问'}</button>
              </div>
            )}
          </div>
        )}

        {/* 深入理解模式 */}
        {mode === 'deep_dive' && (
          <div className="deep-dive-mode">
            <div className="selected-content">
              <strong>选中内容：</strong>
              <textarea value={editableText} onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => onUpdateSession(id, { editableText: e.target.value })} placeholder="选中的文本内容..." rows={3} className="editable-text" disabled={isAnalyzing} style={{ pointerEvents: 'auto' }} />
            </div>
            {aiQuickAnalysis && (<div className="previous-scan"><strong>第一轮快速浏览：</strong><div className="previous-content">{aiQuickAnalysis.substring(0, 150)}...</div></div>)}
            {!aiDeepAnalysis && !isAnalyzing && (
              <div className="start-analysis-section">
                <button onClick={() => onStartDeepDive(id)} className="start-analysis-btn" disabled={!editableText.trim()} style={{ pointerEvents: 'auto' }}>开始深入理解分析</button>
                <small style={{ display: 'block', marginTop: '0.5rem', color: '#666', fontSize: '12px' }}>提示：可以先修改上方文本内容，然后点击开始分析</small>
              </div>
            )}
            {isAnalyzing && (<div className="analyzing-status"><div className="mini-spinner"></div><span>正在生成深入理解分析...</span></div>)}
            {aiDeepAnalysis && (
              <div className="deep-dive-result">
                <div className="deep-dive-header">
                  <h4>深入理解分析</h4>
                  <div className="speech-controls">
                    <button onClick={(e: React.MouseEvent) => { e.preventDefault(); e.stopPropagation(); generateSpeech(aiDeepAnalysis); }} disabled={isGeneratingSpeech} className="speech-btn" title="转为语音" style={{ pointerEvents: 'auto' }}>{isGeneratingSpeech ? '生成中...' : '🔊'}</button>
                    {currentAudio && (<button onClick={(e: React.MouseEvent) => { e.preventDefault(); e.stopPropagation(); stopAudio(); }} className="stop-speech-btn" title="停止播放" style={{ pointerEvents: 'auto' }}>⏹</button>)}
                  </div>
                </div>
                <div className="analysis-content"><ReactMarkdown remarkPlugins={[remarkMath]} rehypePlugins={[rehypeKatex]}>{aiDeepAnalysis}</ReactMarkdown></div>
                <div className="deep-dive-actions">
                  <button onClick={(e: React.MouseEvent) => { e.preventDefault(); e.stopPropagation(); onSendToCanvas(id, 'deep_dive'); }} className="send-to-canvas-btn" disabled={isAnalyzing || session.sentDeepDiveToCanvas} style={{ pointerEvents: 'auto' }}>{session.sentDeepDiveToCanvas ? '\u2713 已发送' : '🎨 发送到画布'}</button>
                </div>
              </div>
            )}
            {aiDeepAnalysis && (
              <div className="follow-up-section">
                <label>继续追问：</label>
                <textarea value={question} onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => onUpdateSession(id, { question: e.target.value })} placeholder="继续追问更深入的问题..." rows={2} disabled={status === 'questioning'} style={{ pointerEvents: 'auto' }} />
                <button onClick={(e: React.MouseEvent) => { e.preventDefault(); e.stopPropagation(); onAskQuestion(id); }} disabled={status === 'questioning' || !question.trim()} className="ask-btn" style={{ pointerEvents: 'auto' }}>{status === 'questioning' ? '思考中...' : '追问'}</button>
              </div>
            )}
          </div>
        )}

        {/* 正常问答模式 */}
        {mode === 'normal' && (
          <div className="normal-qa-mode">
            <div className="selected-content">
              <strong>选中内容（可编辑）：</strong>
              <textarea value={editableText} onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => onUpdateSession(id, { editableText: e.target.value })} placeholder="在这里编辑选中的文本内容..." rows={3} className="editable-text" disabled={status === 'questioning'} style={{ pointerEvents: 'auto' }} />
            </div>
            {hasHighlight && (<div className="highlight-preview-section"><div className="highlight-status">已选择文本区域，保存后将创建高亮标注</div></div>)}
            <div className="question-section">
              <label>请提问：</label>
              <div className="preset-prompts">
                <div className="preset-buttons">
                  {PRESET_PROMPTS.slice(0, 3).map((prompt, pIndex) => (
                    <button key={pIndex} type="button" className="preset-btn" onClick={(e: React.MouseEvent) => { e.preventDefault(); e.stopPropagation(); onUpdateSession(id, { question: prompt }); }} title={prompt} disabled={status === 'questioning'} style={{ pointerEvents: 'auto' }}>
                      {prompt.length > 15 ? prompt.substring(0, 15) + '...' : prompt}
                    </button>
                  ))}
                </div>
              </div>
              <textarea value={question} onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => onUpdateSession(id, { question: e.target.value })} placeholder="基于选中内容提问..." rows={2} disabled={status === 'questioning'} style={{ pointerEvents: 'auto' }} />
              <button onClick={(e: React.MouseEvent) => { e.preventDefault(); e.stopPropagation(); onAskQuestion(id); }} disabled={status === 'questioning' || !question.trim() || !editableText.trim()} className="ask-btn" style={{ pointerEvents: 'auto' }}>{status === 'questioning' ? '思考中...' : '提问'}</button>
            </div>
          </div>
        )}

        {answer && (
          <div className="answer-section">
            <div className="answer-header">
              <strong>AI回答：</strong>
              <div className="speech-controls">
                <button onClick={(e: React.MouseEvent) => { e.preventDefault(); e.stopPropagation(); generateSpeech(answer); }} disabled={isGeneratingSpeech} className="speech-btn" title="转为语音" style={{ pointerEvents: 'auto' }}>{isGeneratingSpeech ? '生成中...' : '🔊'}</button>
                {currentAudio && (<button onClick={(e: React.MouseEvent) => { e.preventDefault(); e.stopPropagation(); stopAudio(); }} className="stop-speech-btn" title="停止播放" style={{ pointerEvents: 'auto' }}>⏹</button>)}
              </div>
            </div>
            <div className="answer-content"><ReactMarkdown remarkPlugins={[remarkMath]} rehypePlugins={[rehypeKatex]}>{answer}</ReactMarkdown></div>
          </div>
        )}

        {(aiQuickAnalysis || aiDeepAnalysis || answer) && (
          <div className="save-annotation-section" style={{ pointerEvents: 'auto', position: 'relative', zIndex: 100 }} onMouseDown={(e: React.MouseEvent) => { e.stopPropagation(); }}>
            <div className="tag-selection">
              <label>选择标注类型：</label>
              <select value={selectedTag || 'note'} onChange={(e: React.ChangeEvent<HTMLSelectElement>) => { e.preventDefault(); e.stopPropagation(); onUpdateSession(id, { selectedTag: e.target.value }); }} className="tag-select" style={{ pointerEvents: 'auto' }}>
                {Object.entries(ANNOTATION_TAGS).map(([key, config]: [string, any]) => (
                  <option key={key} value={key}>{config.name}</option>
                ))}
              </select>
            </div>
            <button type="button" onClick={handleSaveAnnotation} disabled={(quickScanSaved && mode === 'quick_scan' && !aiDeepAnalysis) || (!aiQuickAnalysis && !aiDeepAnalysis && (!answer || answer.trim().length === 0))} className="save-annotation-btn" style={{ pointerEvents: 'auto', position: 'relative', zIndex: 101 }}>
              {quickScanSaved && mode === 'quick_scan' && !aiDeepAnalysis ? '已保存快速浏览' :
               mode === 'quick_scan' ? '保存快速浏览' :
               mode === 'deep_dive' ? (quickScanSaved ? '保存深入理解（更新）' : '保存深入理解') :
               hasHighlight ? '保存为高亮标注' : '保存为标注'}
            </button>
            {quickScanSaved && mode === 'quick_scan' && !aiDeepAnalysis && (
              <small style={{ display: 'block', marginTop: '0.5rem', color: '#28a745', fontSize: '12px' }}>快速浏览已保存，可继续进行深入理解</small>
            )}
          </div>
        )}

        {session.conversationHistory && session.conversationHistory.length > 2 && (
          <div className="conversation-history">
            <div className="history-header">
              <strong>对话历史：</strong>
              <span className="history-count">({Math.floor(session.conversationHistory.length / 2)} 轮对话)</span>
            </div>
            <div className="history-content">
              {session.conversationHistory.slice(0, -2).map((msg: any, msgIndex: number) => (
                <div key={msgIndex} className={`history-message ${msg.role}`}>
                  <div className="message-role">{msg.role === 'user' ? '问' : '答'}:</div>
                  <div className="message-content">
                    {msg.role === 'user' ?
                      msg.content.replace(/基于以下文档内容回答问题：.*?请根据文档内容准确回答问题[^。]*。回答要简洁准确。/s, '').trim() :
                      msg.content
                    }
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        </div>
      )}
    </div>
  );
}

interface MultiQAPanelProps {
  qaSessions: QASession[];
  onUpdateSession: (id: string, updates: Partial<QASession>) => void;
  onRemoveSession: (id: string) => void;
  onAskQuestion: (id: string) => void;
  onSaveAsAnnotation: (id: string) => void;
  onStartQuickScan: (id: string) => void;
  onStartDeepDive: (id: string) => void;
  onConvertToDeepDive: (id: string) => void;
  onSendToCanvas: (id: string, mode: string) => void;
  [key: string]: any;
}

// 多会话问答管理组件
export default function MultiQAPanel({
  qaSessions,
  onUpdateSession,
  onRemoveSession,
  onAskQuestion,
  onSaveAsAnnotation,
  onStartQuickScan,
  onStartDeepDive,
  onConvertToDeepDive,
  onSendToCanvas
}: MultiQAPanelProps) {
  if (!qaSessions || qaSessions.length === 0) {
    return null;
  }

  return (
    <>
      {qaSessions.map((session, index) => (
        <QASessionCard
          key={session.id}
          session={session}
          index={index}
          onUpdateSession={onUpdateSession}
          onRemoveSession={onRemoveSession}
          onAskQuestion={onAskQuestion}
          onSaveAsAnnotation={onSaveAsAnnotation}
          onStartQuickScan={onStartQuickScan}
          onStartDeepDive={onStartDeepDive}
          onConvertToDeepDive={onConvertToDeepDive}
          onSendToCanvas={onSendToCanvas}
        />
      ))}
    </>
  );
}
